import {
  FC,
  FocusEventHandler,
  PropsWithChildren,
  useEffect,
  useRef,
  useState,
} from 'react';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { IconButton, InputBase } from '@mui/material';

import googleGeocoderService from '@/Organisms/SearchBar/Input/GeocoderPlace.service';

import Options from '../DrawerOptions/DrawerOptions';
import useGoogleAutocomplete from './useGoogleAutocomplete';

import useStyles from './styles';

type Props = {
  placeholder: string;
  ariaLabel?: string;
  Icon?: FC<PropsWithChildren<unknown>>;
  onAutocompleteBlur?: FocusEventHandler<
    HTMLTextAreaElement | HTMLInputElement
  >;
  onOptionChange?: Function;
  onChange?: Function;
  cssOption?: string;
  name?: string;
  defaultValue?: string;
  isReadOnly?: boolean;
  suggestAllTypesOfAddress?: boolean;
};

const InputGoogle: FC<PropsWithChildren<Props>> = ({
  placeholder,
  ariaLabel,
  Icon,
  onOptionChange,
  onAutocompleteBlur,
  onChange,
  cssOption,
  name = 'defaultAutocompleteName',
  defaultValue = '',
  isReadOnly = false,
  suggestAllTypesOfAddress = false,
  ...rest
}) => {
  const inputRef = useRef();
  const [inputValue, setInputValue] = useState(defaultValue);
  const [value] = useState(['Aucun résultat']);
  const [drawerOptions, setDrawerOptions] = useState([
    [{ text: 'Aucun résultat' }],
  ]);
  const [isOptionOpen, setIsOptionOpen] = useState(false);
  const { options } = useGoogleAutocomplete(
    inputValue,
    value,
    suggestAllTypesOfAddress
  );

  const { classes } = useStyles({ isReadOnly });

  useEffect(() => {
    setInputValue(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        inputRef?.current &&
        // eslint-disable-next-line spaced-comment
        //@ts-ignore
        !inputRef?.current?.contains(event.target)
      ) {
        setIsOptionOpen(false);
      }
    }
    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [inputRef]);

  useEffect(() => {
    if (options?.length > 0) {
      const newOptions = onOptionChange(
        options
          .map((opt) => opt.description)
          .filter((opt) => opt !== undefined),
        inputValue
      );
      setDrawerOptions(
        newOptions?.length === 0 ? [[{ text: 'Aucun résultat' }]] : newOptions
      );
    } else {
      setDrawerOptions([[{ text: 'Aucun résultat' }]]);
    }
  }, [options, inputValue, onOptionChange]);

  const onInputChange = (e) => {
    const input = e.target.value;
    setInputValue(input);
    if (input === '') {
      setIsOptionOpen(false);
      onChange(undefined);
    } else {
      setIsOptionOpen(true);
    }
  };

  const onOptionClick = async (option) => {
    const placeId = options.find(
      ({ description }) => option === description
    )?.place_id;
    setInputValue(option);
    setIsOptionOpen(false);
    onChange(await googleGeocoderService(option, placeId));
  };

  return (
    <div className={classes.root} ref={inputRef}>
      {Icon && (
        <IconButton
          aria-label="recherhcher"
          disableRipple
          disableFocusRipple
          classes={{
            root: classes.iconButton,
          }}
          size="large"
        >
          <Icon />
        </IconButton>
      )}
      <InputBase
        className={classes.input}
        placeholder={placeholder}
        classes={{ input: classes.placeholder }}
        onChange={onInputChange}
        value={inputValue}
        name={name}
        onBlur={onAutocompleteBlur}
        inputProps={{
          ...rest,
          'aria-label': `${ariaLabel}`,
          autoComplete: 'off',
          readOnly: isReadOnly,
        }}
      />
      {isOptionOpen && (
        <div className={`${classes.options} ${cssOption}`}>
          <Options
            options={drawerOptions}
            onClick={onOptionClick}
            Icon={LocationOnIcon}
          />
        </div>
      )}
    </div>
  );
};

export default InputGoogle;
